import {
    type ApiContainerFollowUpChoice,
    type ApiContainerFollowUpOffer,
    type ApiContainerFollowUpStop,
    type ApiContainerFollowUpSource,
    type ApiPropagateContainerResult,
    EnumContainerOrderAction,
} from '@Model';
import { type LinkableField } from '@synqo/frontend-component-library';

// Declared here rather than imported from @Model: TypeGen does not cover the command payloads.
/** The `orders:propagate_container.v1` payload: the changed row, the values it named before and the rows to write. */
export interface PropagateContainerCommand {
    order_id: string;
    container_action_ids: string[];
    old_container_type: LinkableField;
    old_container: LinkableField;
    old_content: LinkableField;
    old_billed_as: LinkableField;
    stops: ApiContainerFollowUpStop[];
}

/**
 * What happens to the containers in the follow-up and when, e.g. "Leerung 18.10." or "Abholung ohne Termin (3×)",
 * and whether the follow-up loses the new number on another row.
 */
// @ai-generated 2026-10-16T14:35:32+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function followUpEvent(row: ApiContainerFollowUpStop, newNumber: string): string {
    const count = row.container_action_ids.length;
    const when = row.date ? dayAndMonth(row.date) : 'ohne Termin';
    const newContainerTakenOffNote = row.new_container_is_taken_off_another_row
        ? `, Nr. ${newNumber} wird dort an anderer Stelle entfernt`
        : '';
    return `${actionNoun(row)} ${when}${count > 1 ? ` (${count}×)` : ''}${newContainerTakenOffNote}`;
}

/** The ticked emptyings followed by the chosen pickup, if any, as the follow-ups to write. */
// @ai-generated 2026-10-30T14:40:58+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
export function chosenStops(
    offer: ApiContainerFollowUpOffer,
    choice: ApiContainerFollowUpChoice | undefined,
    ticked: ReadonlySet<string>,
): ApiContainerFollowUpStop[] {
    const emptyings = offer.emptyings.filter((emptying) => ticked.has(emptying.order_id));
    return choice?.pickup ? [...emptyings, choice.pickup] : emptyings;
}

/** What the placement carries over, one line per changed value, e.g. "Nr. — → Nr. 9". */
// @ai-generated 2026-10-16T13:56:39+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function changeLines(source: ApiContainerFollowUpSource): string[] {
    const lines: string[] = [];
    if (source.container_type.foreign_key !== source.old_container_type.foreign_key) {
        lines.push(
            `${source.old_container_type.display_string || '—'} → ${source.container_type.display_string || '—'}`,
        );
    }
    if (source.container.foreign_key !== source.old_container.foreign_key) {
        lines.push(`Nr. ${source.old_container.display_string || '—'} → Nr. ${source.container.display_string || '—'}`);
    }
    if (source.content.foreign_key !== source.old_content.foreign_key) {
        lines.push(`${source.old_content.display_string || '—'} → ${source.content.display_string || '—'}`);
    }
    if (source.billed_as.foreign_key !== source.old_billed_as.foreign_key) {
        lines.push(
            `Berechnen als ${source.old_billed_as.display_string || '—'} → ${source.billed_as.display_string || '—'}`,
        );
    }
    return lines;
}

/** How many orders took the values over, followed by why the others did not. */
// @ai-generated 2026-10-16T13:56:39+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function propagationSummary(result: ApiPropagateContainerResult): string {
    const count = result.applied.length;
    const applied = `Übernommen in ${count} ${count === 1 ? 'Auftrag' : 'Aufträge'}.`;
    return [applied, ...result.skipped.map((skip) => skip.reason)].join(' ');
}

function actionNoun(row: ApiContainerFollowUpStop): string {
    if (row.action === EnumContainerOrderAction.Empty) {
        return 'Leerung';
    }
    return row.is_exchange ? 'Tausch' : 'Abholung';
}

function dayAndMonth(date: string): string {
    return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}
